'use client';

import React, { forwardRef, useEffect, useRef } from 'react';

const keyPoints = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    label: 'Locally owned & operated',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    label: 'Safe for surfaces & plants',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    label: 'Fast scheduling',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    label: 'Free estimates',
  },
];

const HeroManifesto = forwardRef<HTMLElement>((_, ref) => {
  const numberRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const pointsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer1 = setTimeout(() => {
      if (numberRef?.current) {
        numberRef.current.style.animation = 'clipIn 1.4s cubic-bezier(0.25, 1, 0.5, 1) forwards';
      }
    }, 200);

    const timer2 = setTimeout(() => {
      if (subRef?.current) {
        subRef.current.style.animation = 'fadeUp 0.9s cubic-bezier(0.25, 1, 0.5, 1) forwards';
      }
    }, 900);

    const timer3 = setTimeout(() => {
      if (bodyRef?.current) {
        bodyRef.current.style.animation = 'fadeUp 0.9s cubic-bezier(0.25, 1, 0.5, 1) forwards';
      }
    }, 1200);

    const timer4 = setTimeout(() => {
      if (pointsRef?.current) {
        pointsRef.current.style.animation = 'fadeUp 0.9s cubic-bezier(0.25, 1, 0.5, 1) forwards';
      }
    }, 1500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  return (
    <section
      ref={ref}
      id="hero"
      className="min-h-screen grid-bg flex flex-col justify-center relative overflow-hidden"
      style={{
        padding: 'clamp(4rem, 8vw, 8rem) clamp(2rem, 6vw, 6rem)',
        background: 'var(--arctic-white)',
      }}
    >
      {/* Decorative accent line */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{ height: '3px', background: 'var(--accent-blue)' }}
      />
      {/* Issue marker */}
      <div
        className="mb-12 flex items-center gap-4"
        style={{ opacity: 0, animation: 'fadeIn 0.6s ease-out 0.1s forwards' }}
      >
        <div
          style={{
            height: '1px',
            width: '40px',
            background: 'var(--accent-blue)',
          }}
        />
        <span
          style={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--accent-blue)',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          }}
        >
          Lake Country Cleaning / Professional Pressure Washing
        </span>
      </div>
      {/* Main Headline */}
      <div ref={numberRef} style={{ opacity: 0 }}>
        <div
          className="manifesto-number"
          style={{ marginBottom: '0.25rem', fontSize: 'clamp(3rem, 8vw, 6rem)' }}
        >
          Professional Power Washing in Minneapolis
          <span className="manifesto-period">.</span>
        </div>
        <div
          style={{
            display: 'inline-block',
            marginTop: '0.75rem',
            fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 600,
            color: 'var(--electric-blue)',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          ⚡ Same-day service available
        </div>
      </div>
      {/* Sub-headline */}
      <div ref={subRef} style={{ opacity: 0, marginTop: '2rem', maxWidth: '680px' }}>
        <p
          style={{
            fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 400,
            color: 'var(--slate)',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          Professional pressure washing that{' '}
          <span style={{ fontWeight: 700, color: 'var(--slate)' }}>removes years of buildup</span>{' '}
          in a single visit — driveways, siding, decks, and more.
        </p>
      </div>
      {/* Body copy */}
      <div ref={bodyRef} style={{ opacity: 0, marginTop: '1.5rem', maxWidth: '520px' }}>
        <p
          style={{
            fontSize: '1rem',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 400,
            color: 'var(--mist-gray)',
            lineHeight: 1.7,
          }}
        >
          Exposed aggregate. Vinyl siding. Oil-stained concrete.{' '}
          <span style={{ color: 'var(--slate)', fontWeight: 500 }}>
            We restore surfaces fast — and a clean property holds its value.
          </span>
        </p>

        {/* CTA row */}
        <div className="flex flex-wrap gap-4 mt-8">
          <button
            className="btn-primary"
            onClick={() => {
              document.getElementById('stats')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            See Our Services
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
            </svg>
          </button>
          <button
            className="btn-secondary"
            onClick={() => {
              document.getElementById('comparison')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            View Pricing
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      {/* Key Points */}
      <div
        ref={pointsRef}
        style={{ opacity: 0, marginTop: '3rem', maxWidth: '640px' }}
      >
        <div
          style={{
            height: '1px',
            background: 'rgba(0,0,0,0.08)',
            marginBottom: '1.75rem',
          }}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {keyPoints?.map((point) => (
            <div
              key={point?.label}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.625rem',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(0,119,204,0.08)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-blue)',
                  flexShrink: 0,
                }}
              >
                {point?.icon}
              </div>
              <span
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: 'var(--slate)',
                  lineHeight: 1.35,
                }}
              >
                {point?.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

HeroManifesto.displayName = 'HeroManifesto';
export default HeroManifesto;